import { getDropboxErrorCode, getDropboxErrorKey } from '@/lib/dropbox/errors';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ProBadge } from '@/components/ProBadge';
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Upload, Clock, Crown, Video, Mic, Settings, Scissors, Target, X, Info, Zap, CheckCircle2, AlertTriangle } from "lucide-react";
import { useState, useEffect, useRef, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { VideoMetadata, formatVideoDuration, formatVideoDurationInMinutes, formatHoursMinutes } from "@/lib/video-metadata";
import { useHasFeature } from "@/lib/useHasFeature";
import { resolveProcessingMode } from '@/lib/upload/resolveProcessingMode';
import ProcessingModeSelector from '@/components/upload/ProcessingModeSelector';
import { MyProject, useCreateMyProjectMutation, useCreateMyLibraryMutation, useMyDataQuery, useMyBrandKitsQuery } from "@/generated/graphql";
import { useTrackProjectCreated } from "@/hooks/use-track-project-created";
import { useToast } from "@/hooks/use-toast";
import { client } from "@/lib/apollo-client";
import { UploadProgress } from "@/hooks/use-upload-file";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import BrandKitScrollRow from "@/components/BrandKitScrollRow";
import { computeRemainingQuota } from "@/lib/billing/computeRemainingQuota";
import { computeProcessingUsage } from "@/lib/quota/computeProcessingUsage";
import { sanitizeProCreationSelections } from "@/lib/quota/sanitizeProCreationSelections";
import ProcessingUsagePanel from "@/components/ProcessingUsagePanel";
import { useQuotaUpsellDialog } from "@/hooks/use-quota-upsell-dialog";
import { FrameioVideoPicker } from '@/components/upload/FrameioVideoPicker';
import { UploadVideoPreview } from '@/components/upload/UploadVideoPreview';
import { buildFrameioImportInputs, runFrameioImport, sumSelectedDuration } from '@/lib/frameio/planFrameioImport';
import { getShareImportErrorKey, isShareImportPlatform, shareImportCopyNamespace } from '@/lib/shareImport/shareImport';
import { MAX_VIDEO_DURATION_SECONDS } from '@/lib/video-limits';
import { CLIP_FORMATS, CLIP_FORMAT_LABEL_KEYS, DEFAULT_CLIP_FORMAT } from '@/lib/projects/clipFormats';
import { useYoutubeAudioTracks } from '@/hooks/use-youtube-audio-tracks';
import { buildAudioSelection, defaultTrackCode, normalizeTracks } from '@/lib/upload/buildAudioSelection';
import { primaryLanguage } from '@/lib/languages/subtitleLanguages';
import { YoutubeAudioFields } from '@/components/upload/YoutubeAudioFields';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: (project?: MyProject) => void;
  videoMetadata?: VideoMetadata | null;
  uploadProgress?: UploadProgress | null;
  isUploadComplete?: boolean;
  isUploaded?: boolean;
  mode?: 'project' | 'library';
}

/**
 * The form used to be one flat run of ten equally-weighted settings, so the
 * essential decision (name it, create it) read no louder than an optional PRO
 * toggle. Grouping them under quiet headings gives the scroll a shape without
 * hiding anything behind tabs or an accordion.
 */
const FormSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-4 rounded-xl border border-border/60 bg-card/30 p-4 sm:p-5">
    <h3 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
      {title}
    </h3>
    {children}
  </section>
);

const UploadModal = ({
  isOpen,
  onClose,
  onUploadComplete,
  videoMetadata,
  uploadProgress,
  isUploadComplete,
  isUploaded,
  mode = 'project',
}: UploadModalProps) => {
  const { t } = useTranslation();
  const isDropbox = videoMetadata?.platform === 'dropbox';
  const [dropboxError, setDropboxError] = useState<string | null>(null);
  // Frame.io shares and MediaSilo reviews: pick videos from a folder listing.
  const metadataPlatform = videoMetadata?.platform;
  const shareImportPlatform = isShareImportPlatform(metadataPlatform) ? metadataPlatform : null;
  const isShareImport = shareImportPlatform !== null;
  const importShare = isShareImport ? videoMetadata?.shareVideos ?? null : null;
  const shareCopy = shareImportCopyNamespace(shareImportPlatform ?? 'frameio');
  const [shareSelectedIds, setShareSelectedIds] = useState<Set<string>>(new Set());
  const [shareImportProgress, setShareImportProgress] = useState<{ current: number; total: number } | null>(null);
  const [shareImportFailures, setShareImportFailures] = useState<Array<{ name: string; errorKey: string }>>([]);
  const isShareImporting = shareImportProgress !== null;
  const [selectedPrompt, setSelectedPrompt] = useState("clipes-virais");
  const [customPrompt, setCustomPrompt] = useState("");
  const [projectName, setProjectName] = useState("");
  const [startTime, setStartTime] = useState("00:00:00");
  const [endTime, setEndTime] = useState("00:00:00");
  const [useTimeSelection, setUseTimeSelection] = useState(false);
  const [timeValidationError, setTimeValidationError] = useState("");
  const [selectedFormat, setSelectedFormat] = useState<string>(DEFAULT_CLIP_FORMAT);
  const [selectedLanguage, setSelectedLanguage] = useState("pt-BR");
  // YouTube only: null track = the default (original); null subtitle = same as audio.
  const [selectedTrackCode, setSelectedTrackCode] = useState<string | null>(null);
  const [subtitleChoice, setSubtitleChoice] = useState<string | null>(null);
  const [selectedBrandKitId, setSelectedBrandKitId] = useState("none");
  const [selectedProcessingMode, setSelectedProcessingMode] = useState("automatic");
  const [removeSilence, setRemoveSilence] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasDefaultBeenSet = useRef(false);
  // PRO gating applies in every mode (project and library alike). Free users
  // see pro options disabled + a PRO badge and can never submit pro values.
  const hasProFeatures = useHasFeature('pro_features');
  const { mode: effectiveProcessingMode, automaticLocked } = resolveProcessingMode({
    isPro: hasProFeatures,
    requested: selectedProcessingMode,
  });
  const { toast } = useToast();
  const { dialog: quotaUpsellDialog, openQuotaUpsell, handleQuotaError } = useQuotaUpsellDialog({
    onBeforeUpgrade: onClose,
  });

  // Utility functions for formatting upload progress
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const formatSpeed = (bytesPerSecond: number): string => {
    return `${formatBytes(bytesPerSecond)}/s`;
  };

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  // YouTube audio track + subtitle language (every other source keeps the plain language select).
  const isYoutube = metadataPlatform === 'youtube' && !isShareImport;
  const audioLookup = useYoutubeAudioTracks(isYoutube ? videoMetadata?.url ?? null : null, isYoutube && isOpen);
  const audioTracks = normalizeTracks(audioLookup.tracks);
  const usableTracks = audioTracks.filter((track) => track.transcribable && track.code && track.language);
  const lookupDecidesAudio = audioLookup.status === 'ok' && usableTracks.length > 0;
  // buildAudioSelection also forces language = audio language for free users.
  const audioSelection = isYoutube
    ? buildAudioSelection({
        tracks: audioTracks,
        lookupStatus: audioLookup.status,
        selectedTrackCode,
        manualAudioLanguage: selectedLanguage,
        subtitleLanguage: subtitleChoice ?? '',
        hasPro: hasProFeatures,
      })
    : null;
  const youtubeAudioBlocked = isYoutube && (audioLookup.status === 'loading' || !audioSelection);

  // GraphQL queries and mutations
  const { data: myData, refetch: refetchMyData, loading: myDataLoading } = useMyDataQuery();
  const trackProjectCreated = useTrackProjectCreated(mode, isOpen);
  const { data: brandKitsData, loading: brandKitsLoading } = useMyBrandKitsQuery({
    skip: !hasProFeatures, // Only fetch brand kits for pro users
  });
  const [createMyProject, { loading: isCreatingProject, error: createProjectError }] = useCreateMyProjectMutation();
  const [createMyLibrary, { loading: isCreatingLibrary, error: createLibraryError }] = useCreateMyLibraryMutation();
  const isCreating = mode === 'library' ? isCreatingLibrary : isCreatingProject;
  const isBusy = isCreating || isShareImporting;
  const createError = mode === 'library' ? createLibraryError : createProjectError;

  // Extract usage data from myData. Project-mode processing/clips figures come
  // from computeProcessingUsage (shared with the news-PDF upload modal); the
  // library-mode figures stay inline below.
  const subscriptionData = myData?.myData?.subscription;
  const plan = subscriptionData?.plan;
  const currentMonthLibraryVideoSeconds = subscriptionData?.current_month_library_video_seconds || 0;
  const maxLibraryVideoSeconds = plan?.max_library_video_seconds || 0;
  const libraryUsagePercentage = maxLibraryVideoSeconds > 0 ? (currentMonthLibraryVideoSeconds / maxLibraryVideoSeconds) * 100 : 0;

  const promptOptions = [
    { id: "clipes-virais", label: t('uploadModal.viralClips'), description: t('uploadModal.viralClipsDesc') },
    { id: "frases-memoraveis", label: t('uploadModal.memorableQuotes'), description: t('uploadModal.memorableQuotesDesc') },
    { id: "clipes-topicos", label: t('uploadModal.topicClips'), description: t('uploadModal.topicClipsDesc') },
    { id: "anuncios-produtos", label: t('uploadModal.productAds'), description: t('uploadModal.productAdsDesc') },
    { id: "depoimento", label: t('uploadModal.testimonials'), description: t('uploadModal.testimonialsDesc') },
    { id: "outro", label: t('uploadModal.other'), description: t('uploadModal.otherDesc') }
  ];

  // Helper function to format seconds to HH:mm:ss
  const formatTimeToHHMMSS = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to get current video time
  const getCurrentTime = () => {
    if (videoRef.current) {
      return formatTimeToHHMMSS(videoRef.current.currentTime);
    }
    return "00:00:00";
  };

  // Helper function to convert HH:mm:ss to seconds
  const timeToSeconds = (timeStr: string): number => {
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  };

  // Helper function to format time input with mask
  const formatTimeInput = (value: string): string => {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');

    // Limit to 6 digits (HHMMSS)
    const limited = numbers.slice(0, 6);

    // Format as HH:mm:ss
    if (limited.length >= 4) {
      return `${limited.slice(0, 2)}:${limited.slice(2, 4)}:${limited.slice(4, 6)}`;
    } else if (limited.length >= 2) {
      return `${limited.slice(0, 2)}:${limited.slice(2)}`;
    } else {
      return limited;
    }
  };

  // Handle time input change with mask
  const handleTimeChange = (value: string, setTime: (time: string) => void) => {
    const formatted = formatTimeInput(value);
    setTime(formatted);
  };

  // Auto-complete time input when it loses focus
  const handleTimeBlur = (time: string, setTime: (time: string) => void) => {
    if (!time.includes(':')) {
      // If user only typed numbers, auto-complete the format
      const numbers = time.replace(/\D/g, '');
      if (numbers.length === 1) {
        setTime(`0${numbers}:00:00`);
      } else if (numbers.length === 2) {
        setTime(`${numbers}:00:00`);
      } else if (numbers.length === 3) {
        setTime(`${numbers.slice(0, 2)}:0${numbers.slice(2)}:00`);
      } else if (numbers.length === 4) {
        setTime(`${numbers.slice(0, 2)}:${numbers.slice(2, 4)}:00`);
      } else if (numbers.length === 5) {
        setTime(`${numbers.slice(0, 2)}:${numbers.slice(2, 4)}:0${numbers.slice(4)}`);
      } else if (numbers.length === 6) {
        setTime(`${numbers.slice(0, 2)}:${numbers.slice(2, 4)}:${numbers.slice(4, 6)}`);
      }
    } else if (time.split(':').length < 3) {
      // If format is incomplete, complete it
      const parts = time.split(':');
      if (parts.length === 2) {
        setTime(`${parts[0]}:${parts[1]}:00`);
      } else if (parts.length === 1) {
        setTime(`${parts[0]}:00:00`);
      }
    }
  };

  // Validate time selection
  const validateTimeSelection = (start: string, end: string): string => {
    if (!start.match(/^\d{2}:\d{2}:\d{2}$/) || !end.match(/^\d{2}:\d{2}:\d{2}$/)) {
      return t('uploadModal.invalidTimeFormat');
    }

    const startSeconds = timeToSeconds(start);
    const endSeconds = timeToSeconds(end);

    if (startSeconds >= endSeconds) {
      return t('uploadModal.startMustBeLessThanEnd');
    }

    // Enforce minimum segment length of more than 1 minute
    const segmentDuration = endSeconds - startSeconds;
    if (segmentDuration <= 600) {
      return t('uploadModal.segmentMustBeOver10Min');
    }

    if (videoMetadata && endSeconds > videoMetadata.duration) {
      return t('uploadModal.endTimeExceedsDuration');
    }

    return "";
  };

  // Calculate effective duration for processing (reactive)
  const effectiveDuration = useMemo(() => {
    if (!videoMetadata) return 0;
    if (importShare) return sumSelectedDuration(importShare.videos, shareSelectedIds);

    if (useTimeSelection && hasProFeatures && !validateTimeSelection(startTime, endTime)) {
      const startSeconds = timeToSeconds(startTime);
      const endSeconds = timeToSeconds(endTime);
      return endSeconds - startSeconds;
    }

    return videoMetadata.duration;
  }, [videoMetadata, importShare, shareSelectedIds, useTimeSelection, hasProFeatures, startTime, endTime]);

  // Compute processing/clips usage and remaining-quota checks against the
  // effective duration (shared logic with the news-PDF upload modal).
  const processingUsage = computeProcessingUsage(subscriptionData, effectiveDuration);
  const exceedsProcessingLimit = mode !== 'library' && !myDataLoading && processingUsage.exceedsProcessingLimit;
  const remainingLibraryVideoSeconds = computeRemainingQuota(maxLibraryVideoSeconds, currentMonthLibraryVideoSeconds);
  const exceedsLibraryLimit = mode === 'library' && !myDataLoading && maxLibraryVideoSeconds > 0 && effectiveDuration > remainingLibraryVideoSeconds;

  // Handle project creation
  const handleCreateProject = async () => {
    if (!videoMetadata) return;
    setDropboxError(null);
    if (isDropbox && !hasProFeatures) {
      openQuotaUpsell('paid_feature');
      return;
    }
    if (isShareImport && !hasProFeatures) {
      openQuotaUpsell('paid_feature');
      return;
    }

    if (!canSubmit()) {
      showSubmitBlocker();
      return;
    }

    // Check if upload is still in progress
    if (!isUploadComplete) {
      toast({
        title: t('uploadModal.uploadInProgress'),
        description: t('uploadModal.waitForUpload'),
        variant: "default",
      });
      return;
    }

    if (exceedsProcessingLimit) {
      openQuotaUpsell('processing_time');
      return;
    }
    if (exceedsLibraryLimit) {
      openQuotaUpsell('library_video_time');
      return;
    }

    try {
      // Ensure we're using the real upload URL, not blob URL
      if (videoMetadata.url.startsWith('blob:')) {
        toast({
          title: t('uploadModal.internalError'),
          description: t('uploadModal.videoUrlNotProcessed'),
          variant: "destructive",
        });
        return;
      }

      // Submit-side pro gate: free users can never send pro values.
      const proSelections = sanitizeProCreationSelections({
        promptId: selectedPrompt,
        brandKitId: selectedBrandKitId,
        processingMode: effectiveProcessingMode,
        removeSilence,
      }, hasProFeatures);
      const selectedPromptData = promptOptions.find(option => option.id === proSelections.promptId);
      const baseInput = {
        name: projectName.trim(),
        base_video_url: videoMetadata.url, // This should be the real upload URL now
        duration_seconds: effectiveDuration,
        format: selectedFormat,
        language: selectedLanguage,
        ...(audioSelection ?? {}),
        prompt: proSelections.promptId === "outro" ? customPrompt.trim() : selectedPromptData?.description,
        remove_silence: proSelections.removeSilence,
        ...(useTimeSelection && hasProFeatures && !timeValidationError && {
          start_time: timeToSeconds(startTime),
          end_time: timeToSeconds(endTime),
        }),
      };

      if (importShare) {
        await importShareVideos(importShare.videos, {
          format: selectedFormat,
          language: selectedLanguage,
          prompt: baseInput.prompt,
          remove_silence: proSelections.removeSilence,
          processing_mode: proSelections.processingMode,
          brand_kit_id: proSelections.brandKitId === "none" ? null : proSelections.brandKitId,
        });
        return;
      }

      if (mode === 'library') {
        const libraryInput = {
          ...baseInput,
          processing_mode: proSelections.processingMode,
          brand_kit_id: proSelections.brandKitId === "none" ? null : proSelections.brandKitId,
        };
        const result = await createMyLibrary({
          variables: { input: libraryInput }
        });

        if (isDropbox && (result.errors?.length || !result.data?.createMyLibrary)) {
          throw result.errors?.length ? { errors: result.errors } : new Error('DROPBOX_UNAVAILABLE');
        }

        if (result.data?.createMyLibrary) {
          const newLibrary = result.data.createMyLibrary;

          trackProjectCreated();

          await client.refetchQueries({ include: ['MyLibraries'] });
          await refetchMyData();

          toast({
            title: t('uploadModal.projectCreatedSuccess'),
            description: t('uploadModal.projectAddedToList', { name: newLibrary.name }),
            duration: 5000,
          });

          onUploadComplete(newLibrary as unknown as MyProject);
        }
      } else {
        const input = {
          ...baseInput,
          processing_mode: proSelections.processingMode,
          brand_kit_id: proSelections.brandKitId === "none" ? null : proSelections.brandKitId,
        };

        const result = await createMyProject({
          variables: { input }
        });

        if (isDropbox && (result.errors?.length || !result.data?.createMyProject)) {
          throw result.errors?.length ? { errors: result.errors } : new Error('DROPBOX_UNAVAILABLE');
        }

        if (result.data?.createMyProject) {
          const newProject = result.data.createMyProject;

          trackProjectCreated();

          await client.refetchQueries({ include: ['MyProjects'] });
          await refetchMyData();

          toast({
            title: t('uploadModal.projectCreatedSuccess'),
            description: t('uploadModal.projectAddedToList', { name: newProject.name }),
            duration: 5000,
          });

          onUploadComplete(newProject);
        }
      }
    } catch (error) {
      if (isDropbox) {
        if (handleQuotaError(error)) return;
        if (getDropboxErrorCode(error) === 'DROPBOX_PRO_REQUIRED') openQuotaUpsell('paid_feature');
        else setDropboxError(getDropboxErrorKey(error));
        return;
      }
      console.error(`Error creating ${mode}:`, error);
      handleQuotaError(error);
    }
  };

  // Share imports: one create per selected video, run sequentially. Every error is
  // handled here so it never reaches the Dropbox/generic catch above.
  const importShareVideos = async (
    videos: NonNullable<VideoMetadata['shareVideos']>['videos'],
    sharedInput: {
      format: string;
      language: string;
      prompt: string | undefined;
      remove_silence: boolean;
      processing_mode: string;
      brand_kit_id: string | null;
    },
  ) => {
    const inputs = buildFrameioImportInputs({ videos, selectedIds: shareSelectedIds, baseInput: sharedInput });
    // Inputs follow share order filtered by selection, so this maps an input index back to its asset id.
    const selectedVideoIds = videos.filter((video) => shareSelectedIds.has(video.asset_id)).map((video) => video.asset_id);
    setShareImportFailures([]);
    setShareImportProgress({ current: 0, total: inputs.length });
    try {
      const summary = await runFrameioImport({
        inputs,
        onProgress: (current, total) => setShareImportProgress({ current, total }),
        create: async (input) => {
          // A thrown ApolloError propagates as-is; resolved GraphQL errors and
          // an empty payload are rethrown so quota/PRO classification applies.
          if (mode === 'library') {
            const result = await createMyLibrary({ variables: { input } });
            if (result.errors?.length) throw { errors: result.errors };
            if (!result.data?.createMyLibrary) throw new Error(shareImportPlatform === 'mediasilo' ? 'MEDIASILO_UNAVAILABLE' : 'FRAMEIO_UNAVAILABLE');
          } else {
            const result = await createMyProject({ variables: { input } });
            if (result.errors?.length) throw { errors: result.errors };
            if (!result.data?.createMyProject) throw new Error(shareImportPlatform === 'mediasilo' ? 'MEDIASILO_UNAVAILABLE' : 'FRAMEIO_UNAVAILABLE');
          }
          trackProjectCreated();
        },
      });
      // Deselect what was created before anything else can throw, so a retry
      // only resubmits the videos that did not make it.
      const createdIds = new Set(summary.createdIndices.map((index) => selectedVideoIds[index]));
      if (createdIds.size > 0) {
        setShareSelectedIds((current) => new Set([...current].filter((id) => !createdIds.has(id))));
      }
      setShareImportProgress(null);
      if (summary.created.length > 0) {
        await client.refetchQueries({ include: [mode === 'library' ? 'MyLibraries' : 'MyProjects'] });
      }
      await refetchMyData();

      if (summary.stoppedBy === 'pro') openQuotaUpsell('paid_feature');
      else if (summary.stoppedBy === 'quota') handleQuotaError(summary.stopError);

      const failures = [
        ...summary.failed.map(({ name, error }) => ({ name, errorKey: getShareImportErrorKey(error, shareImportPlatform ?? 'frameio') })),
        ...summary.skipped.map((name) => ({
          name,
          errorKey: summary.stoppedBy === 'pro' ? `${shareCopy}.summaryStoppedPro` : `${shareCopy}.summaryStopped`,
        })),
      ];
      setShareImportFailures(failures);
      const allSucceeded = failures.length === 0 && summary.stoppedBy === null;
      toast({
        title: allSucceeded
          ? t(`${shareCopy}.summaryAllCreated`, { count: summary.created.length })
          : t(`${shareCopy}.summaryPartial`, { created: summary.created.length, failed: failures.length }),
        variant: allSucceeded ? 'default' : 'destructive',
        duration: 5000,
      });
      if (allSucceeded) onUploadComplete();
    } catch (error) {
      toast({ title: t(getShareImportErrorKey(error, shareImportPlatform ?? 'frameio')), variant: 'destructive' });
    } finally {
      setShareImportProgress(null);
    }
  };

  // Check if form can be submitted
  const canSubmit = () => {
    const nameCondition = isShareImport ? shareSelectedIds.size > 0 : projectName.trim() !== "";
    const baseConditions = nameCondition && !isBusy && isUploadComplete && !youtubeAudioBlocked;
    const customPromptCondition = selectedPrompt !== "outro" || customPrompt.trim() !== "";

    if (useTimeSelection && hasProFeatures && !isShareImport) {
      const error = validateTimeSelection(startTime, endTime);
      return !error && baseConditions && customPromptCondition;
    }
    return baseConditions && customPromptCondition;
  };

  const getSubmitBlockerMessage = (): string | null => {
    if (isShareImport && shareSelectedIds.size === 0) return t(`${shareCopy}.noSelection`);
    if (!isShareImport && !projectName.trim()) return t('uploadModal.projectNameRequired');
    if (selectedPrompt === 'outro' && !customPrompt.trim()) {
      return t('uploadModal.customPromptRequired');
    }
    if (!isUploadComplete) return t('uploadModal.waitForUpload');
    if (youtubeAudioBlocked) {
      return t(audioLookup.status === 'loading' ? 'uploadModal.audioLookupPending' : 'uploadModal.chooseAudioTrack');
    }
    if (useTimeSelection && hasProFeatures && !isShareImport && timeValidationError) {
      return timeValidationError;
    }
    return null;
  };

  const showSubmitBlocker = () => {
    const description = getSubmitBlockerMessage();
    if (!description) return;

    toast({
      title: !isUploadComplete
        ? t('uploadModal.uploadInProgress')
        : t('uploadModal.cannotCreateYet'),
      description,
      variant: !isUploadComplete ? 'default' : 'destructive',
    });
  };

  const submitBlockerMessage = !isBusy ? getSubmitBlockerMessage() : null;

  // Set default project name and end time based on video metadata
  useEffect(() => {
    if (videoMetadata) {
      setDropboxError(null);
      setProjectName(videoMetadata.title || `Video ${formatVideoDuration(videoMetadata.duration)}`);
      const videos = videoMetadata.shareVideos?.videos ?? [];
      // Single-video links start selected; folders start empty so nothing is imported by accident.
      setShareSelectedIds(new Set(
        videos.length === 1 && videos[0].duration_seconds <= MAX_VIDEO_DURATION_SECONDS ? [videos[0].asset_id] : [],
      ));
      setShareImportProgress(null);
      setShareImportFailures([]);
      setSelectedTrackCode(null);
      setSubtitleChoice(null);
      setSelectedLanguage('pt-BR');
    }
  }, [videoMetadata]);

  // Validate time selection when times change
  useEffect(() => {
    if (useTimeSelection && hasProFeatures) {
      const error = validateTimeSelection(startTime, endTime);
      setTimeValidationError(error);
    } else {
      setTimeValidationError("");
    }
  }, [startTime, endTime, useTimeSelection, hasProFeatures, videoMetadata]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Reset any errors when modal opens
      setTimeValidationError("");
      // Reset the default flag when modal opens
      hasDefaultBeenSet.current = false;
      // Auto-select default brand kit when modal opens (if data is already available)
      if (brandKitsData?.myBrandKits && hasProFeatures) {
        const defaultBrandKit = brandKitsData.myBrandKits.find(kit => kit.is_default);
        if (defaultBrandKit) {
          setSelectedBrandKitId(defaultBrandKit.id);
          hasDefaultBeenSet.current = true;
        }
      }
    } else {
      // Reset form state when modal closes
      if (brandKitsData?.myBrandKits && hasProFeatures) {
        const defaultBrandKit = brandKitsData.myBrandKits.find(kit => kit.is_default);
        setSelectedBrandKitId(defaultBrandKit ? defaultBrandKit.id : "none");
      } else {
        setSelectedBrandKitId("none");
      }
      // Reset the flag for next time
      hasDefaultBeenSet.current = false;
    }
  }, [isOpen, brandKitsData?.myBrandKits, hasProFeatures]);

  // Clear custom prompt when switching away from "outro" option
  useEffect(() => {
    if (selectedPrompt !== "outro") {
      setCustomPrompt("");
    }
  }, [selectedPrompt]);

  // Auto-select default brand kit when brand kits data loads (after modal is already open)
  // But only if we haven't already set a default for this modal session
  useEffect(() => {
    if (brandKitsData?.myBrandKits && hasProFeatures && isOpen && !hasDefaultBeenSet.current) {
      const defaultBrandKit = brandKitsData.myBrandKits.find(kit => kit.is_default);
      if (defaultBrandKit) {
        setSelectedBrandKitId(defaultBrandKit.id);
        hasDefaultBeenSet.current = true;
      }
    }
  }, [brandKitsData?.myBrandKits, hasProFeatures, isOpen]);

  // Escape closes the modal. It is a hand-rolled overlay rather than a shadcn
  // Dialog, so nothing gave it keyboard dismissal before.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isShareImporting) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, isShareImporting]);

  // Prevent body scrolling when modal is open (page-level scroll within overlay like AccountSettings)
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={isShareImporting ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-modal-title"
        className="flex w-full max-w-4xl max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)] flex-col overflow-hidden bg-background rounded-lg shadow-lg border border-primary/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div
            id="upload-modal-title"
            className="text-xl sm:text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent"
          >
            {t('uploadModal.title')}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isShareImporting} aria-label={t('common.cancel')}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6 pb-4">
          {/* Video Preview — hidden for share imports: they pick videos from a listing. */}
          {!isShareImport && <UploadVideoPreview videoMetadata={videoMetadata} videoRef={videoRef} />}

          {/* Upload Progress */}
          {uploadProgress && (
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-4 border border-primary/20 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Upload className="w-5 h-5 text-primary animate-pulse" />
                  <span className="text-sm font-semibold text-foreground">{t('uploadModal.uploadingFile')}</span>
                </div>
              </div>

              <Progress value={uploadProgress.percentage} className="h-2 mb-3" />

              <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>{t('uploadModal.progress')}</span>
                    <span className="font-medium">{uploadProgress.percentage.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('uploadModal.transferred')}</span>
                    <span className="font-medium">{formatBytes(uploadProgress.uploadedBytes)} / {formatBytes(uploadProgress.totalBytes)}</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>{t('uploadModal.speed')}</span>
                    <span className="font-medium">{formatSpeed(uploadProgress.uploadSpeed)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('uploadModal.remainingTime')}</span>
                    <span className="font-medium">{formatTime(uploadProgress.remainingTime)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Upload Complete */}
          {isUploadComplete && !uploadProgress && isUploaded && (
            <div className="flex items-center justify-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-400" aria-hidden="true" />
              <p className="text-xs font-medium text-emerald-300">
                {t('uploadModal.uploadComplete')}
              </p>
            </div>
          )}

          {/* Audio Player (if video doesn't have audio) */}
          {!isDropbox && !isShareImport && videoMetadata?.best_video_format && !videoMetadata.best_video_format.has_audio && videoMetadata.best_audio_format && (
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-4 border border-primary/20 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <Mic className="w-5 h-5 text-primary" />
                <span className="text-sm font-semibold text-foreground">{t('uploadModal.videoAudio')}</span>
              </div>
              <audio
                src={videoMetadata.best_audio_format.url}
                crossOrigin="anonymous"
                controls
                controlsList="nodownload"
                className="w-full"
                preload="metadata"
              >
                {t('uploadModal.browserNotSupportAudio')}
              </audio>
            </div>
          )}

          <FormSection title={t('uploadModal.sectionVideo')}>
            {importShare && (
              <FrameioVideoPicker
                share={importShare}
                copyNamespace={shareCopy}
                selectedIds={shareSelectedIds}
                onChange={setShareSelectedIds}
                maxDurationSeconds={MAX_VIDEO_DURATION_SECONDS}
                disabled={isShareImporting}
              />
            )}

            {/* Project Name */}
            {mode !== 'library' && !isShareImport && (
            <div className="space-y-3">
              <label className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2 text-foreground">
                <Video className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                {t('uploadModal.projectName')}
              </label>
              <Input
                placeholder={t('uploadModal.projectNamePlaceholder')}
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="h-10 sm:h-12 text-sm sm:text-base shadow-sm focus:shadow-glow border-primary/20 focus:border-primary/40 transition-all duration-300"
              />
            </div>
            )}

            {/* Settings Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-3">
                <label className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2 text-foreground">
                  <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  {t('uploadModal.videoFormat')}
                </label>
                <Select value={selectedFormat} onValueChange={setSelectedFormat}>
                  <SelectTrigger className="h-10 sm:h-12 text-sm sm:text-base shadow-sm focus:shadow-glow border-primary/20 focus:border-primary/40 transition-all duration-300">
                    <SelectValue placeholder={t('uploadModal.selectFormat')} />
                  </SelectTrigger>
                  <SelectContent>
                    {CLIP_FORMATS.map((format) => (
                      <SelectItem key={format} value={format}>{t(CLIP_FORMAT_LABEL_KEYS[format])}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isYoutube ? (
                <YoutubeAudioFields
                  tracks={audioTracks}
                  showTrackSelect={audioLookup.status === 'ok' && audioTracks.length >= 2}
                  showManualAudio={!lookupDecidesAudio}
                  selectedTrackCode={selectedTrackCode ?? defaultTrackCode(audioTracks)}
                  onTrackChange={setSelectedTrackCode}
                  manualAudioLanguage={selectedLanguage}
                  onManualAudioChange={setSelectedLanguage}
                  audioLanguage={audioSelection?.audio_language ?? null}
                  subtitleLanguage={
                    subtitleChoice &&
                    (hasProFeatures || primaryLanguage(subtitleChoice) === primaryLanguage(audioSelection?.audio_language))
                      ? subtitleChoice
                      : audioSelection?.audio_language ?? ''
                  }
                  onSubtitleChange={setSubtitleChoice}
                  hasPro={hasProFeatures}
                />
              ) : (
              <div className="space-y-3">
                <label className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2 text-foreground">
                  <Mic className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  {t('uploadModal.videoLanguage')}
                </label>
                <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                  <SelectTrigger className="h-10 sm:h-12 text-sm sm:text-base shadow-sm focus:shadow-glow border-primary/20 focus:border-primary/40 transition-all duration-300">
                    <SelectValue placeholder={t('uploadModal.selectLanguage')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pt-BR">{t('uploadModal.portuguese')}</SelectItem>
                    <SelectItem value="en-US">{t('uploadModal.english')}</SelectItem>
                    <SelectItem value="es-ES">{t('uploadModal.spanish')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              )}
            </div>
          </FormSection>

          <FormSection title={t('uploadModal.sectionHowToCut')}>
            {/* Processing Mode Selector */}
            <div className="space-y-3">
                <label className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2 text-foreground">
                  <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  {t('copyLibrary.processingMode')}
                </label>
                <ProcessingModeSelector
                  value={effectiveProcessingMode}
                  onChange={setSelectedProcessingMode}
                  automaticLocked={automaticLocked}
                />
              </div>

            {/* Prompt Selector */}
            <div className="space-y-4">
              <label className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2 text-foreground">
                <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                {t('uploadModalExtra.whatYouLookFor')}
                <ProBadge />
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {promptOptions.map((option) => {
                  const isProOption = option.id !== "clipes-virais";
                  const isDisabled = isProOption && !hasProFeatures;

                  return (
                    <div
                      key={option.id}
                      className={`p-3 sm:p-4 rounded-xl border-2 transition-all duration-300 relative ${
                        isDisabled
                          ? 'border-border bg-background/30 opacity-60 cursor-not-allowed'
                          : selectedPrompt === option.id
                            ? 'border-primary bg-gradient-to-br from-primary/10 to-primary/5 shadow-glow cursor-pointer hover:scale-105'
                            : 'border-border hover:border-primary/40 bg-background/50 cursor-pointer hover:scale-105'
                      }`}
                      onClick={() => !isDisabled && setSelectedPrompt(option.id)}
                    >
                      <h3 className="font-semibold text-xs sm:text-sm text-foreground flex items-center justify-between gap-2">
                        <span className="inline-block">{option.label}</span>
                        {!isDisabled && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="w-4 h-4 text-muted-foreground hover:text-primary cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs max-w-xs">{option.description}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </h3>
                      {isDisabled && (
                        <div className="absolute top-2 right-2">
                          <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                            <Crown className="w-3 h-3 mr-1" />
                            PRO
                          </Badge>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Custom Prompt Input (when "outro" is selected) */}
            {selectedPrompt === "outro" && (
              <div className="space-y-3">
                <label className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2 text-foreground">
                  <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  {t('uploadModalExtra.describeWhatYouLookFor')}
                </label>
                <textarea
                  placeholder={t('uploadModalExtra.customPromptPlaceholder')}
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  className="min-h-[100px] w-full rounded-md border border-primary/20 bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 shadow-sm focus:shadow-glow focus:border-primary/40 transition-all duration-300"
                  rows={4}
                />
                {customPrompt.trim() === "" && (
                  <p className="text-xs text-muted-foreground">
                    {t('uploadModalExtra.customPromptHint')}
                  </p>
                )}
              </div>
            )}

            {/* Processing Settings */}
            {mode !== 'library' && !isShareImport && (
            <div className="space-y-4 sm:space-y-6">
              <div className="space-y-3">
                <label className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2 text-foreground">
                  <Video className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  {t('uploadModalExtra.videoExtentLabel')}
                  <ProBadge />
                </label>
                <Select
                  value={useTimeSelection ? "segment" : "full"}
                  onValueChange={(value) => {
                    if (value === "segment" && hasProFeatures) {
                      setUseTimeSelection(true);
                    } else {
                      setUseTimeSelection(false);
                    }
                  }}
                >
                  <SelectTrigger className="h-10 sm:h-12 text-sm sm:text-base shadow-sm focus:shadow-glow border-primary/20 focus:border-primary/40 transition-all duration-300">
                    <SelectValue placeholder={t('uploadModalExtra.selectExtension')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">{t('uploadModalExtra.fullVideo')}</SelectItem>
                    <SelectItem value="segment" disabled={!hasProFeatures}>
                      <div className="flex items-center gap-2">
                        <span>{t('uploadModalExtra.videoSegment')}</span>
                        {!hasProFeatures && (
                          <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                            <Crown className="w-3 h-3 mr-1" />
                            PRO
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Time Selection (when segment is selected) */}
              {useTimeSelection && hasProFeatures && (
                <div className="space-y-4 p-4 rounded-xl border border-primary shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <Scissors className="w-5 h-5 text-primary" />
                    <span className="text-sm font-semibold text-foreground">Selecionar Trecho do Vídeo</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Start Time */}
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-muted-foreground">Tempo de Início (HH:mm:ss)</label>
                      <div className="flex gap-2">
                        <Input
                          value={startTime}
                          onChange={(e) => handleTimeChange(e.target.value, setStartTime)}
                          onBlur={(e) => handleTimeBlur(e.target.value, setStartTime)}
                          placeholder="00:00:00"
                          pattern="[0-9]{2}:[0-9]{2}:[0-9]{2}"
                          className={`text-sm font-mono text-center ${timeValidationError ? 'border-red-500 focus:border-red-500' : ''}`}
                          maxLength={8}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setStartTime(getCurrentTime())}
                          className="shrink-0"
                          disabled={!videoRef.current}
                        >
                          <Target className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">{t('uploadModalExtra2.timeInputHint')}</p>
                    </div>

                    {/* End Time */}
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-muted-foreground">Tempo de Fim (HH:mm:ss)</label>
                      <div className="flex gap-2">
                        <Input
                          value={endTime}
                          onChange={(e) => handleTimeChange(e.target.value, setEndTime)}
                          onBlur={(e) => handleTimeBlur(e.target.value, setEndTime)}
                          placeholder="00:00:00"
                          pattern="[0-9]{2}:[0-9]{2}:[0-9]{2}"
                          className={`text-sm font-mono text-center ${timeValidationError ? 'border-red-500 focus:border-red-500' : ''}`}
                          maxLength={8}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEndTime(getCurrentTime())}
                          className="shrink-0"
                          disabled={!videoRef.current}
                        >
                          <Target className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">{t('uploadModalExtra2.timeInputHint')}</p>
                    </div>
                  </div>

                  {timeValidationError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-4">
                      <p className="text-xs text-red-800 text-center">
                        ⚠️ {timeValidationError}
                      </p>
                    </div>
                  )}

                  <div className="text-xs text-muted-foreground text-center mt-3">
                    💡 {t('uploadModalExtra2.captureTimeHint')}
                  </div>
                </div>
              )}

              {/* Clips Count Selector */}
              {/* <div className="space-y-4">
                <label className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2 text-foreground">
                  <Scissors className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  Número de clipes
                  {clipsCount[0] > 5 && (
                    <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                      <Crown className="w-3 h-3 mr-1" />
                      PRO
                    </Badge>
                  )}
                </label>
                <div className="p-4 sm:p-6 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/20 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs sm:text-sm font-medium text-muted-foreground">5 clipes</span>
                    <span className="text-base sm:text-lg font-bold text-primary">{clipsCount[0]} clipes</span>
                    <span className="text-xs sm:text-sm font-medium text-muted-foreground">20 clipes</span>
                  </div>
                  <Slider
                    value={clipsCount}
                    onValueChange={setClipsCount}
                    max={20}
                    min={5}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                    <span>5</span>
                    <span>10</span>
                    <span>15</span>
                    <span>20</span>
                  </div>
                </div>
              </div> */}
            </div>
            )}

            {/* Remove Silence (PRO) */}
            <div className="flex items-center justify-between gap-4 rounded-xl border-2 border-border bg-background/50 p-3 sm:p-4">
              <div className="flex-1">
                <label
                  htmlFor="remove-silence"
                  className="text-sm sm:text-base font-semibold mb-1 flex items-center gap-2 text-foreground"
                >
                  <Scissors className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  {t('uploadModal.removeSilence')}
                  {!hasProFeatures && (
                    <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                      <Crown className="w-3 h-3 mr-1" />
                      {t('common.pro')}
                    </Badge>
                  )}
                </label>
                <p className="text-xs text-muted-foreground">
                  {t('uploadModal.removeSilenceDescription')}
                </p>
              </div>
              <Switch
                id="remove-silence"
                checked={hasProFeatures && removeSilence}
                onCheckedChange={setRemoveSilence}
                disabled={!hasProFeatures}
              />
            </div>
          </FormSection>

          <FormSection title={t('uploadModal.sectionBranding')}>
            {/* Brand Kit Selection */}
            <div className="space-y-3">
              <label className="text-sm sm:text-base font-semibold mb-2 flex items-center gap-2 text-foreground">
                <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                {t('uploadModal.brandKit')}
                <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">{t('common.pro')}</Badge>
              </label>
              {brandKitsLoading && hasProFeatures ? (
                <div className="h-10 sm:h-12 border border-primary/20 rounded-md flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  <span className="ml-2 text-sm text-muted-foreground">{t('uploadModal.loadingBrandKits')}</span>
                </div>
              ) : hasProFeatures ? (
                <div className="space-y-3">
                  <div
                    className={`cursor-pointer rounded-lg border-2 transition-colors p-2 text-center text-sm ${
                      selectedBrandKitId === "none"
                        ? 'border-primary bg-primary/10'
                        : 'border-gray-600 hover:border-gray-400'
                    }`}
                    onClick={() => setSelectedBrandKitId("none")}
                  >
                    {t('uploadModal.noBrandKit')}
                  </div>
                  {(brandKitsData?.myBrandKits || []).length > 0 && (
                    <BrandKitScrollRow
                      kits={brandKitsData?.myBrandKits || []}
                      selectedId={selectedBrandKitId}
                      onSelect={setSelectedBrandKitId}
                      showDefault
                    />
                  )}
                </div>
              ) : (
                <div className="opacity-60 cursor-not-allowed bg-muted rounded-md p-3 text-sm text-center text-muted-foreground">
                  {t('uploadModal.brandKitProOnly')}
                </div>
              )}
              {hasProFeatures && (
                <p className="text-xs text-muted-foreground">
                  {t('uploadModal.brandKitProTip')}
                </p>
              )}
            </div>

            {/* Brand Kit Suggestion for users with no brand kits */}
            {hasProFeatures && !brandKitsLoading && brandKitsData?.myBrandKits?.length === 0 && (
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-4 border border-primary/20 shadow-sm">
                <div className="flex items-start gap-3">
                  <Crown className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-foreground mb-1">
                      {t('uploadModalExtra2.createBrandKitTitle')}
                    </h3>
                    <p className="text-xs text-muted-foreground mb-3">
                      {t('uploadModalExtra2.createBrandKitDescription')}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs bg-primary/10 text-primary border-primary/30 hover:bg-primary/20"
                      onClick={() => window.open('/templates', '_blank')}
                    >
                      <Crown className="w-3 h-3 mr-1" />
                      {t('uploadModalExtra2.createBrandKit')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </FormSection>

          {/* Usage Info */}
          {mode !== 'library' ? (
          <ProcessingUsagePanel
            loading={myDataLoading}
            usage={processingUsage}
            effectiveDurationSeconds={effectiveDuration}
            hasVideo={!!videoMetadata}
            exceedsProcessingLimit={exceedsProcessingLimit}
            selectedSegmentDurationSeconds={
              useTimeSelection && hasProFeatures && !isShareImport && !timeValidationError ? effectiveDuration : null
            }
          />
          ) : (
          <div className="bg-gradient-to-br from-background/80 to-background/60 p-4 sm:p-6 rounded-xl border border-primary/20 shadow-sm space-y-4">
            {myDataLoading ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  <span className="ml-2 text-sm text-muted-foreground">{t('uploadModalExtra2.loadingUsageData')}</span>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Library Video Time Usage */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-primary" />
                      {t('uploadModalExtra2.processingTime')}
                    </span>
                    <span className="font-medium">
                      {formatHoursMinutes(currentMonthLibraryVideoSeconds)} / {formatHoursMinutes(maxLibraryVideoSeconds)}
                    </span>
                  </div>
                  <Progress value={libraryUsagePercentage} className="h-2" />
                </div>
              </div>
            )}

            <p className="text-xs sm:text-sm text-center text-primary font-medium">
              {t('uploadModalExtra2.videoWillConsume', { duration: videoMetadata ? formatVideoDurationInMinutes(effectiveDuration) : "" })}
            </p>

            {exceedsLibraryLimit && (
              <div className="border border-red-900 rounded-lg p-4 mt-2 space-y-3">
                <p className="text-sm text-center">
                  {t('uploadModalExtra2.insufficientTime', { required: formatVideoDurationInMinutes(effectiveDuration), available: formatVideoDurationInMinutes(remainingLibraryVideoSeconds) })}
                  <br/>
                  {t('uploadModalExtra2.considerUpgrading')}
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <Button
                    variant="gradient"
                    size="lg"
                    className="text-md"
                    onClick={() => window.location.href = '/plans'}
                  >
                    <Crown className="w-3 h-3 mr-1" />
                    {t('uploadModalExtra2.upgradePlan')}
                  </Button>
                </div>
              </div>
            )}
          </div>
          )}

          {shareImportFailures.length > 0 && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 space-y-1" data-testid="frameio-failures">
              <p className="text-xs font-semibold text-red-300">{t(`${shareCopy}.failedListTitle`)}</p>
              <ul className="space-y-0.5">
                {shareImportFailures.map((failure, index) => (
                  // Folder shares can hold two videos with the same name.
                  <li key={`${index}-${failure.name}`} className="text-xs text-red-300">
                    {failure.name}: {t(failure.errorKey)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Error Display — Frame.io failures are listed per video above. */}
          {((createError && !isShareImport) || dropboxError) && (
            <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" aria-hidden="true" />
              <p className="text-xs text-red-300">
                {isDropbox ? t(dropboxError ?? getDropboxErrorKey(createError)) : t('uploadModalExtra2.createProjectError', { error: createError?.message })}
              </p>
            </div>
          )}

          {quotaUpsellDialog}
        </div>

        {/* Action bar — pinned so the primary action stays reachable instead of
            sitting below every optional setting. */}
        <div className="flex-shrink-0 border-t bg-background/95 backdrop-blur-sm p-4 sm:px-6 space-y-3">
          {submitBlockerMessage && (
            <p
              id="create-project-blocker"
              className="text-center text-sm text-muted-foreground"
            >
              {submitBlockerMessage}
            </p>
          )}
          <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-4">
            <Button variant="outline" onClick={onClose} size="lg" className="flex-1" disabled={isBusy}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="gradient"
              size="lg"
              className="flex-1 tour-create-project"
              onClick={handleCreateProject}
              disabled={isBusy || youtubeAudioBlocked}
              aria-describedby={submitBlockerMessage ? 'create-project-blocker' : undefined}
            >
              {shareImportProgress
                ? t(`${shareCopy}.creatingProgress`, shareImportProgress)
                : isCreating
                ? t('uploadModalExtra2.creating')
                : !isUploadComplete
                  ? t('uploadModalExtra2.waitingUpload')
                  : t('uploadModalExtra2.createProject')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
